/**
 * Shared `fetch`-wrapper factory for `admin-module-*` client `api.ts` files.
 *
 * Every module's `api.ts` currently hand-rolls the same base-URL register +
 * `unwrap` pair, split into two message-format variants observed across the
 * 11 modules (see `.superpowers/sdd/03-kit-extraction/task-1-brief.md`):
 *
 * - `'server-message'` (pipelines, templates): parses a JSON `{error|message}`
 *   body and falls back through `message → statusText → raw text`.
 * - `'raw'` (classification, admins, channels, telegram, conversations'
 *   two-line inline copy, payment, insights, identity, blacklist's one-line
 *   inline copy): throws `` `request failed ${status}: ${text || statusText}` ``.
 *
 * This wave only ADDS the package — no `admin-module-*` has been migrated to
 * consume it yet (parity is preserved by construction: existing modules keep
 * their own copies until a later wave swaps them in one at a time).
 */
export function createModuleApi(opts = {}) {
    const errorFormat = opts.errorFormat ?? 'raw';
    let baseUrlRef = '';
    async function extractServerError(r) {
        try {
            const data = (await r.json());
            return data.error ?? data.message ?? r.statusText;
        }
        catch {
            const text = await r.text().catch(() => '');
            return text || r.statusText;
        }
    }
    async function unwrap(r) {
        if (!r.ok) {
            if (errorFormat === 'server-message')
                throw new Error(await extractServerError(r));
            const text = await r.text().catch(() => '');
            throw new Error(`request failed ${r.status}: ${text || r.statusText}`);
        }
        return r.json();
    }
    function fetchRaw(path, init) {
        return fetch(`${baseUrlRef}${path}`, { credentials: 'include', ...init });
    }
    function bodyInit(method, body) {
        return body === undefined
            ? { method }
            : { method, headers: { 'content-type': 'application/json' }, body: JSON.stringify(body) };
    }
    return {
        setBaseUrl: (url) => {
            baseUrlRef = url;
        },
        baseUrl: () => baseUrlRef,
        extractServerError,
        unwrap,
        fetchRaw,
        get: async (path) => unwrap(await fetchRaw(path)),
        post: async (path, body) => unwrap(await fetchRaw(path, bodyInit('POST', body))),
        patch: async (path, body) => unwrap(await fetchRaw(path, bodyInit('PATCH', body))),
        put: async (path, body) => unwrap(await fetchRaw(path, bodyInit('PUT', body))),
        del: (path) => fetchRaw(path, { method: 'DELETE' }),
    };
}
//# sourceMappingURL=module-api.js.map